import { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { LocationIcon, ProfileIcon } from "src/assets/Icons";
import { CANCELLATION_TAG_CONFIG, type CancellationTag } from "src/Modules/UserHistory/pages/OrderDetails/components/HotelTransactionDetails/constants";

const GuestTag: FC<{ name: string }> = ({ name }) => {
  return (
    <span className="bg-background-minimal inline-flex items-center gap-3 rounded-full py-1 pe-5 ps-1">
      <span className="bg-background-default flex size-8 shrink-0 items-center justify-center rounded-full">
        <ProfileIcon className="text-font-subtle size-5" aria-hidden="true" />
      </span>
      <span className="text-font-default text-base font-medium">{name}</span>
    </span>
  );
};

const StatTile: FC<{ label: string; value: string }> = ({ label, value }) => {
  return (
    <div className="bg-background-minimal flex min-w-28 flex-col gap-1 rounded-md px-4 py-3">
      <span className="text-12 text-font-subtle font-medium uppercase tracking-wider">{label}</span>
      <span className="text-font-default text-lg font-medium">{value}</span>
    </div>
  );
};

type RoomRowData = {
  title: string;
  count: number;
  mealPlan: string | null;
  cancellationTags: CancellationTag[];
};

const RoomRow: FC<{ room: RoomRowData; roomIndex: number }> = ({ room, roomIndex }) => {
  const { t } = useTranslation();
  const heading =
    room.count > 1
      ? t("UserHistory.hotel.roomHeadingCount", { index: roomIndex + 1, count: room.count })
      : t("UserHistory.hotel.roomHeading", { index: roomIndex + 1 });
  return (
    <li className="border-border-default bg-background-default flex w-full flex-col gap-5 rounded-lg border p-4 md:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="bg-background-secondary text-font-inverted text-12 rounded-full px-3 py-1 font-semibold uppercase tracking-wider">
          {heading}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-8">
        <div className="flex flex-col gap-1.5">
          <span className="text-12 text-font-subtle font-medium uppercase tracking-wider">{t("UserHistory.hotel.roomType")}</span>
          <span className="text-font-default text-base font-medium">{room.title}</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-12 text-font-subtle font-medium uppercase tracking-wider">{t("UserHistory.hotel.mealPlan")}</span>
          <span className="text-font-default text-base font-medium">{room.mealPlan ?? "—"}</span>
        </div>
      </div>
       {room.cancellationTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
            {room.cancellationTags.map((tag, i) => {
              const config = CANCELLATION_TAG_CONFIG.find((c) => c.select(tag));
              return (
                <span key={`tag-${i}`} className={`${config?.style ?? ""} inline-flex w-fit items-center gap-1.5 rounded-sm px-3 py-1.5`}>
                  {config ? config.display(tag) : tag.label}
                </span>
              );
            })}
          </div>
        )}
    </li>
  );
};

const HotelDetailsCard: FC<{
  data: {
    name: string | null;
    address: string | null;
    nights: string;
    rooms: string;
    roomsData: RoomRowData[];
    guests: string[];
  };
}> = ({
  data,
}) => {
  const { t } = useTranslation();
  return (
    <div className="border-border-default bg-background-default flex flex-col gap-6 rounded-lg border p-4 md:p-6">
      <h2 className="text-font-default text-lg font-medium">{t("UserHistory.hotel.hotelDetails")}</h2>

      <div className="flex flex-col items-start gap-4 md:flex-row md:justify-between">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h3 className="text-font-default text-2xl font-semibold md:text-3xl">{data.name ?? "—"}</h3>
          <div className="flex items-center gap-1.5">
            <LocationIcon className="text-font-subtle size-4 shrink-0" aria-hidden="true" />
            <span className="text-14 text-font-subtle">{data.address ?? "—"}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-stretch gap-2">
          <StatTile label={t("UserHistory.hotel.nights")} value={data.nights} />
          <StatTile label={t("UserHistory.hotel.rooms")} value={data.rooms} />
        </div>
      </div>

      {data.roomsData.length > 0 && (
        <ul className="flex flex-col gap-3" role="list">
          {data.roomsData.map((room, i) => (
            <RoomRow key={`room-${i}`} room={room} roomIndex={i} />
          ))}
        </ul>
      )}

      <div className="border-border-default flex flex-col gap-3 border-t pt-5">
        <span className="text-12 text-font-subtle font-medium uppercase tracking-wider">{t("UserHistory.hotel.guestNames")}</span>
        {data.guests.length > 0 ? (
          <ul className="flex flex-wrap gap-2" role="list">
            {data.guests.map((g, i) => (
              <li key={`guest-${i}`}>
                <GuestTag name={g} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-14 text-font-minimal italic">{t("UserHistory.hotel.noGuestInfo")}</p>
        )}
      </div>
    </div>
  );
};

export default memo(HotelDetailsCard);
